import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { AngularEditorConfig } from '@kolkov/angular-editor';
import { GetDecisionTextService } from 'src/app/modules/committees-management/requests/services/get-decision-text/get-decision-text.service';
import { finalize, takeUntil } from 'rxjs/operators';
import { IFormField } from './IFormField';
import { Subject } from 'rxjs';
import { HttpHandlerService } from 'src/app/core/services/http-handler.service';
import { NumberRange } from 'src/app/core/helpers/NumberRange';
import { Router } from '@angular/router';
import { DecimalNumbersOnly } from "src/app/core/helpers/DecimalNumbers-only";
import { TextDirectionsService } from "src/app/shared/services/text-directions/text-directions.service";
import { ControlTypes } from "./enum";
import { GetDecisionTextModifyRequestsService } from 'src/app/modules/committees-management/change-requests/services/get-decision-text.service';
import { TranslationModuleEnum } from 'src/app/utils/translation-modules.enum';

@Component({
  selector: "app-form-builder-base",
  templateUrl: "./form-builder-base.component.html",
  styleUrls: ["./form-builder-base.component.scss"],
})
export class FormBuilderBaseComponent implements OnInit, OnDestroy {
  private endSub$ = new Subject();

  controlTypes = ControlTypes;
  // text editor configuration
  editorConfig: AngularEditorConfig = {
    editable: true,
    spellcheck: true,
    height: "299px",
    translate: "yes",
    enableToolbar: true,
    showToolbar: true,
    defaultParagraphSeparator: "",
    defaultFontName: "",
    defaultFontSize: "3",
    sanitize: false,
    outline: false,
    toolbarPosition: "top",
    toolbarHiddenButtons: [
      ["subscript", "superscript", "heading", "fontName"],
      [
        "textColor",
        "backgroundColor",
        "customClasses",
        "insertImage",
        "insertVideo",
        "insertHorizontalRule",
        "removeFormat",
        "toggleEditorMode",
      ],
    ],
  };
  form: FormGroup;
  textEditorDefaultValue: string = "";
  translationModulesEnum = TranslationModuleEnum;
  @Input() formFields: IFormField[] = [];
  @Input() language: string = "";

  @Output() formValue: EventEmitter<any> = new EventEmitter();
  @Output() validForm: EventEmitter<boolean> = new EventEmitter();

  constructor(
    private fb: FormBuilder,
    private getDecisionTextService: GetDecisionTextService,
    private getDecisionTextModifyRequestsService: GetDecisionTextModifyRequestsService,
    private httpSer: HttpHandlerService,
    private textDirectionsService: TextDirectionsService,
    private router: Router
  ) {}

  ngOnInit(): void {
    if (this.router.url.includes("/committees-management/modify-requests")) {
      // get the default value of the text editor
      this.textEditorDefaultValue =
        this.getDecisionTextModifyRequestsService.getText();
    } else {
      // get the default value of the text editor
      this.textEditorDefaultValue = this.getDecisionTextService.getText();
    }

    // initialize new committee form controls
    this.initFormControls();

    this.createFormControl();
    this.validForm.emit(this.form.valid);
    this.form.valueChanges.pipe(takeUntil(this.endSub$))
      .subscribe((values) => {
        // console.log(values);
        // add directions to the rich text
        let RichTextInputs = this.formFields.filter(field => field.type == "RichText").map(f => f.key);
        RichTextInputs?.forEach(element => {
          if (values.hasOwnProperty(element)) {
            values[element] = this.textDirectionsService.addDirections(values[element]);
          }
        });
        let ratingSelectors = this.formFields.filter(field => field.type == "RatingSelector").map(f => f.key);
        ratingSelectors?.forEach(element => {
          if (values.hasOwnProperty(element)) {
            values[element] = JSON.stringify({ Option: values[element], Value: +values[`${element}Value`] });
            delete values[`${element}Value`];
          }
        });
        this.formValue.emit(values);

        let isValid = this.form.valid;
        // let isValid = this.form.valid && !this.isNotValid(this.form.value);
        this.validForm.emit(isValid);
      });
  }

  initFormControls() {
    this.form = this.fb.group({});
  }

  // check attributes validations
  isNotValid(obj) {
    for (const key in obj) {
      if (
        obj[key] === null ||
        obj[key] === undefined ||
        obj[key] === "" ||
        obj[key]?.length == 0 ||
        (typeof obj[key] === "string" && this.removeTagsAndSpaces(obj[key]) === ""))
        return true;
    }
    return false;
  }

  removeTagsAndSpaces(inputText) {
    // Remove HTML tags using a regular expression
    const textWithoutTags = inputText.replace(/<[^>]*>/g, "");

    // Remove extra spaces using another regular expression
    const textWithoutSpaces = textWithoutTags.replace(/\s+/g, " ");

    // Trim any leading or trailing spaces
    const trimmedText = textWithoutSpaces.replace(/&#160;/g, "").trim();
    return trimmedText;
  }

  createFormControl() {
    // console.log(this.formFields);
    this.formFields.forEach((control, index) => {
      // temporarily
      let defaultValue = control.key == "DecisionText" ? this.textEditorDefaultValue : null;
      this.formValue.emit({DecisionText: defaultValue });
      control.isRequired ?
        this.form?.addControl(control.key, new FormControl(defaultValue, Validators.required)) :
        this.form?.addControl(control.key, new FormControl(defaultValue));

        let formControl;

        // if (control.type === 'Number') {
        //   formControl = control.isRequired
        //     ? new FormControl(defaultValue, [Validators.required, NumbersOnly()])
        //     : new FormControl(defaultValue, NumbersOnly());
        // } else {
        //   formControl = control.isRequired
        //     ? new FormControl(defaultValue, Validators.required)
        //     : new FormControl(defaultValue);
        // }

        // this.form?.addControl(control.key, formControl);

      if (control.type == "RatingSelector") {
        control.isRequired ?
          this.form?.addControl(`${control.key}Value`, new FormControl(null, [Validators.required, DecimalNumbersOnly()])) :
          this.form?.addControl(`${control.key}Value`, new FormControl(null, [DecimalNumbersOnly()]));
      }
      if (control.key == "DecisionNumber") {
        let defaultDecisionNumber = localStorage.getItem("decisionNumber") ?? 0;
        this.form.controls[control.key].setValue(defaultDecisionNumber);
        this.form.controls[control.key].setValidators(null)
        this.form?.controls[control.key].disable();
      }
      !!control.api && this.getOptions(control.api, index);

      !!control.api && this.getOptions(control.api, index);
    });
  }

  isGettingOptions: boolean = false;

  getOptions(api, optionIndex) {
    this.isGettingOptions = true;
    this.httpSer.get(api)
      .pipe(takeUntil(this.endSub$), finalize(() => {
        this.isGettingOptions = false;
      }))
      .subscribe((res) => {
        if (res)
          this.formFields[optionIndex].options = res;
      });
  }

  rangeSelected(range, control) {
    if (range) {
      // // number
      this.form.controls[`${control}Value`].clearValidators();
      this.form.controls[`${control}Value`].addValidators([
        Validators.required,
        NumberRange(range.start, range.end),
      ]);
      this.form.controls[`${control}Value`].updateValueAndValidity();
    } else {
      this.form.controls[`${control}Value`].clearValidators();
      this.form.controls[`${control}Value`].addValidators([
        Validators.required,
      ]);
      this.form.controls[`${control}Value`].updateValueAndValidity();
    }
  }

  ngOnDestroy(): void {
    this.endSub$.next(null);
    this.endSub$.complete();
  }
  getAttachments(attachments) {

  }
}
